import { useCallback, useEffect, useRef, useState } from 'react';
import { Booking } from '../data/bookings';
import { CategoryKey, Service } from '../data/services';
import {
  cancelBooking,
  createBookings,
  fetchBooking,
  fetchBookings,
  fetchRescheduleAllowance,
  type PaymentMode,
  rescheduleBooking,
  type RescheduleAllowance,
  reviewBooking,
  uploadReviewPhotos,
  toAppBooking,
} from '../services/bookings';
import {
  placeCheckout,
  type CheckoutDto,
  type PlaceCheckoutRequest,
} from '../services/checkout';

/**
 * The customer's bookings, from the platform.
 *
 * ---------------------------------------------------------------------------
 * EMPTY IS AN ANSWER, SEED DATA IS NOT
 * ---------------------------------------------------------------------------
 * Unlike the catalogue - which falls back to the bundled file when the network
 * is out, because a shop window with yesterday's prices is better than an
 * error - this NEVER falls back. Somebody else's example bookings shown as
 * yours is not a degraded experience, it is a lie: you would be looking at a
 * professional who is not coming, at an address that is not yours.
 *
 * So a failure here is a failure, `error` is set, and the screen says so.
 *
 * ---------------------------------------------------------------------------
 * SIGNED OUT MEANS NO LIST, NOT AN ERROR
 * ---------------------------------------------------------------------------
 * These endpoints need a token. Before sign-in there is nothing to fetch and
 * nothing has gone wrong, so the hook stays idle and the screen shows its
 * empty state - which already invites you to browse.
 */
export type UseBookings = {
  bookings: Booking[];
  loading: boolean;
  /** Set when the last attempt failed. Cleared by a successful refresh. */
  error: string | null;
  /** Re-reads the list. Safe to call on focus and on pull-to-refresh. */
  refresh: () => Promise<void>;
  /**
   * Re-reads ONE booking from the detail endpoint and merges it into the list.
   *
   * Worth its own call because `GET /bookings` and `GET /bookings/:id` do not
   * return the same shape: only the detail carries `timeline`, and `startedAt`
   * - the instant the running clock counts from - is read off the `started`
   * event in it. A booking that has only ever been through the list has no
   * start time at all, which is why the in-progress panel showed a bare
   * "Started at" and no timer.
   *
   * Resolves quietly on failure. The list row is already on screen and still
   * correct in every other respect; a failed detail fetch should cost the
   * clock, not the page.
   */
  refreshOne: (id: string) => Promise<void>;
  /**
   * One booking per service id - the platform's unit of dispatch.
   *
   * `addressId` is the server's, not the app's: it is required, and the
   * server reads the city and the pin off that row to decide whether the job
   * can be sent to anyone. `ensureServerAddress` is what turns the locally
   * held address into one.
   */
  book: (input: {
    /** One entry per unit, each with its own length - see `createBookings`. */
    items: { serviceId: string; hours?: number }[];
    addressId: string;
    paymentMode: PaymentMode;
    slotStartAt?: string;
    /**
     * Homingo Coins to spend across the basket, as a count.
     *
     * Charged to the FIRST booking rather than split - see `createBookings`,
     * which explains why splitting an integer balance across N cancellable
     * bookings cannot be reversed cleanly.
     */
    coinsToRedeem?: number;
    /** Order reference, so a retry cannot double-book. */
    idempotencyPrefix: string;
  }) => Promise<Booking[]>;
  /**
   * Places an order through `POST /checkout` - one call for a basket that can
   * span several services, priced as one order rather than fanned out.
   *
   * ---------------------------------------------------------------------------
   * REPLACES `book` FOR EVERYTHING BUT A RECURRING PLAN
   * ---------------------------------------------------------------------------
   * `book` above still exists for a repeating arrangement, which has no
   * `checkout` equivalent - a plan is not an order, it is an instruction that
   * produces one occurrence at a time. For an ordinary or scheduled basket,
   * this is what `HomeScreen`'s checkout handler calls instead of `book`: one
   * `POST /checkout` in place of one `POST /bookings` per service, priced and
   * discounted as the single order the cart quoted.
   *
   * Resolves to whichever bookings the order actually produced -
   * `CheckoutDto.bookingIds` can be SHORTER than `input.items` on a partial
   * success (one line failed to book, the rest went through), and that is not
   * a failure this rejects on. See `services/checkout.ts`'s own note.
   */
  placeCheckoutOrder: (
    input: PlaceCheckoutRequest,
    idempotencyKey: string,
  ) => Promise<{ bookings: Booking[]; order: CheckoutDto }>;
  /**
   * Fetch bookings by id and put them straight in the list.
   *
   * For a basket paid as one: placing it returns no bookings, because none
   * exist until the payment is captured - these are the ids the server made
   * once it was.
   */
  loadBookings: (ids: string[]) => Promise<Booking[]>;
  cancel: (id: string, reason?: string) => Promise<void>;
  /**
   * Move a booking to a new slot, as an ISO instant.
   *
   * ---------------------------------------------------------------------------
   * THERE IS A RESCHEDULE ENDPOINT, AND THIS NOW USES IT
   * ---------------------------------------------------------------------------
   * This used to cancel the booking and create a replacement, on the belief -
   * stated in this very comment - that `POST /bookings/:id/reschedule` did not
   * exist. It does, and always has, mounted on the customer controller beside
   * `cancel`, along with a `GET` companion that says in advance whether a move
   * is allowed at all. See `rescheduleBooking` for what the two-call
   * workaround was costing.
   *
   * Resolves to the SAME booking, moved. The id does not change, the number
   * does not change, and the caller's detail screen keeps pointing at a row
   * that still exists - which was not true before, when the id handed in
   * belonged to a cancelled booking by the time this returned.
   *
   * The elaborate recovery for a cached row missing `addressId`/`paymentMode`
   * is gone with the recreate that needed them. The server knows what the
   * booking is; moving it changes only its slot.
   *
   * Rejects on failure rather than swallowing it: a reschedule that silently
   * does nothing leaves someone believing their visit moved when it did not.
   * The server's refusals arrive as ordinary errors carrying their own
   * message, and that is what the caller shows.
   */
  reschedule: (id: string, slotStartAt: string) => Promise<Booking>;
  /**
   * What the server would allow for this booking, before offering a picker.
   *
   * Not cached in the hook's state: the answer depends on the clock - the
   * six-hour cutoff closes as the slot approaches - so a value read when the
   * list loaded would be wrong by the time somebody taps Reschedule. The
   * caller asks at the moment it opens the sheet.
   */
  rescheduleAllowance: (
    id: string,
    signal?: AbortSignal,
  ) => Promise<RescheduleAllowance>;
  /**
   * Rate the professional, photographs and all.
   *
   * `photos` are what the composer holds - local file URIs, or a key for one
   * already on the platform. Uploading them is this hook's job rather than the
   * caller's: the presign needs the booking id, which is already here, and a
   * screen that had to orchestrate five PUTs before it could submit a review
   * would drop them the first time one failed.
   */
  rate: (
    id: string,
    input: {
      rating: number;
      comment?: string;
      tags?: string[];
      photos?: Array<{ uri: string; key?: string }>;
    },
  ) => Promise<void>;
};

export function useBookings(
  signedIn: boolean,
  services: Service[],
  categoryLabel: (key: CategoryKey) => string,
): UseBookings {
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /* Read by `reschedule` to find the booking being moved without becoming a
     dependency of it - same reasoning as `catalogue` below: rebuilding the
     callback on every list change would fight the identity `HomeScreen`'s own
     `useCallback`s rely on. */
  const bookingsRef = useRef<Booking[]>([]);
  bookingsRef.current = bookings;

  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  /*
   * The catalogue, in a ref rather than a dependency.
   *
   * `toAppBooking` needs it to find a booking's category, but `refresh` must
   * not be rebuilt every time the catalogue object changes - it is called from
   * an effect and from a pull-to-refresh, and a new identity each render would
   * refetch the list on a loop.
   */
  const catalogue = useRef({ services, categoryLabel });
  catalogue.current = { services, categoryLabel };

  const load = useCallback(async () => {
    if (!signedIn) {
      setBookings([]);
      setError(null);
      return;
    }

    setLoading(true);
    try {
      const rows = await fetchBookings();
      if (!alive.current) {
        return;
      }
      setBookings(current => {
        /*
         * Keep any `startedAt` the detail endpoint has already established.
         *
         * `GET /bookings` carries no timeline, so every row it maps has
         * `startedAt: undefined`. Without this, a plain refresh - the header
         * button, a pull-to-refresh, a focus - would blank the instant the
         * running clock counts from and the timer would vanish mid-job.
         *
         * Only ever fills a gap: a start time the list somehow does send wins,
         * because that is the fresher reading.
         */
        const knownStart = new Map(
          current
            .filter(item => item.startedAt)
            .map(item => [item.id, item.startedAt] as const),
        );

        return rows.map(row => {
          const mapped = toAppBooking(
            row,
            catalogue.current.services,
            catalogue.current.categoryLabel,
          );
          return mapped.startedAt
            ? mapped
            : { ...mapped, startedAt: knownStart.get(mapped.id) };
        });
      });
      setError(null);
    } catch (failure) {
      if (alive.current) {
        setError(
          failure instanceof Error
            ? failure.message
            : 'Could not load your bookings.',
        );
      }
    } finally {
      if (alive.current) {
        setLoading(false);
      }
    }
  }, [signedIn]);

  /**
   * One booking, from the endpoint that sends a timeline.
   *
   * Merged into the existing list rather than held beside it, so the detail
   * screen keeps reading the same array as everything else and there is no
   * second copy of a booking to drift out of step.
   *
   * The merge is by id and replaces the row wholesale: the detail response is
   * a superset of the list row, so the mapped booking is at least as complete
   * as the one it replaces.
   */
  const refreshOne = useCallback<UseBookings['refreshOne']>(async id => {
    if (!signedIn || !id) {
      return;
    }

    try {
      const row = await fetchBooking(id);
      if (!alive.current) {
        return;
      }

      const detailed = toAppBooking(
        row,
        catalogue.current.services,
        catalogue.current.categoryLabel,
      );

      setBookings(current =>
        current.some(item => item.id === detailed.id)
          ? current.map(item => (item.id === detailed.id ? detailed : item))
          : /* Not in the list yet - a booking opened straight from a
               notification, say. Keep it rather than dropping it. */
            [detailed, ...current],
      );
    } catch {
      /* Deliberately silent - see the note on `refreshOne` in `UseBookings`. */
    }
  }, [signedIn]);

  /* Signing in and out is the one thing that changes whose list this is. */
  useEffect(() => {
    load().catch(() => {
      /* `load` records its own failure in `error`; nothing else to do with
         a rejection here. */
    });
  }, [load]);

  const book = useCallback<UseBookings['book']>(
    async input => {
      const rows = await createBookings(input);
      const made = rows.map(row =>
        toAppBooking(
          row,
          catalogue.current.services,
          catalogue.current.categoryLabel,
        ),
      );

      /* Put them straight in rather than waiting for a refetch: the customer
         is about to be shown their booking, and a round trip they can see is a
         round trip they will tap through. The next refresh reconciles.

         Keyed by id rather than prepended blindly, because a retry returns the
         bookings that already succeeded - idempotency makes the SERVER stop at
         one row, and without this the list would still show that row twice. */
      if (alive.current) {
        setBookings(current => {
          const fresh = new Set(made.map(booking => booking.id));
          return [...made, ...current.filter(row => !fresh.has(row.id))];
        });
      }
      return made;
    },
    [],
  );

  /**
   * `POST /checkout`, then read back what it actually booked.
   *
   * ---------------------------------------------------------------------------
   * WHY THIS FETCHES EACH BOOKING RATHER THAN TRUSTING THE RESPONSE
   * ---------------------------------------------------------------------------
   * `CheckoutDto` answers with `bookingIds` and the order's frozen money - not
   * full booking rows. So each id is read back through the DETAIL endpoint,
   * the same one `refreshOne` uses and for the same reason: only the detail
   * carries a `timeline`, which is where `startedAt` comes from, and a row
   * this app has only ever seen through `POST /checkout` should not be missing
   * it.
   *
   * `Promise.allSettled`, not `Promise.all`: a detail fetch glitching for one
   * booking must not hide the others - they are real, they were charged for,
   * and the customer is about to be shown their confirmation. The next
   * `refresh` reconciles whichever one was dropped here.
   */
  const loadBookings = useCallback<UseBookings['loadBookings']>(async ids => {
    const settled = await Promise.allSettled(ids.map(id => fetchBooking(id)));
    const made = settled
      .filter(
        (entry): entry is PromiseFulfilledResult<Awaited<ReturnType<typeof fetchBooking>>> =>
          entry.status === 'fulfilled',
      )
      .map(entry =>
        toAppBooking(
          entry.value,
          catalogue.current.services,
          catalogue.current.categoryLabel,
        ),
      );

    if (alive.current && made.length > 0) {
      setBookings(current => {
        const fresh = new Set(made.map(booking => booking.id));
        return [...made, ...current.filter(row => !fresh.has(row.id))];
      });
    }

    return made;
  }, []);

  const placeCheckoutOrder = useCallback<UseBookings['placeCheckoutOrder']>(
    async (input, idempotencyKey) => {
      const order = await placeCheckout(input, idempotencyKey);

      const settled = await Promise.allSettled(
        order.bookingIds.map(id => fetchBooking(id)),
      );
      const made = settled
        .filter(
          (entry): entry is PromiseFulfilledResult<Awaited<ReturnType<typeof fetchBooking>>> =>
            entry.status === 'fulfilled',
        )
        .map(entry =>
          toAppBooking(
            entry.value,
            catalogue.current.services,
            catalogue.current.categoryLabel,
          ),
        );

      /* Put them straight in rather than waiting for a refetch - same reasoning
         as `book`: the customer is about to be shown their booking, and a
         round trip they can see is a round trip they will tap through. Keyed
         by id so a retried checkout (same `idempotencyKey`) does not show the
         order's bookings twice. */
      if (alive.current) {
        setBookings(current => {
          const fresh = new Set(made.map(booking => booking.id));
          return [...made, ...current.filter(row => !fresh.has(row.id))];
        });
      }

      return { bookings: made, order };
    },
    [],
  );

  const cancel = useCallback<UseBookings['cancel']>(async (id, reason) => {
    const row = await cancelBooking(id, reason);
    if (alive.current) {
      setBookings(current =>
        current.map(booking =>
          booking.id === id
            ? toAppBooking(
                row,
                catalogue.current.services,
                catalogue.current.categoryLabel,
              )
            : booking,
        ),
      );
    }
  }, []);

  /*
   * One call, and the booking keeps its identity.
   *
   * This was `cancel` followed by `book`, on the belief that the backend had
   * no reschedule route. It has one - `rescheduleBooking` sets out what the
   * workaround was costing. The short version: a customer paid a late
   * cancellation fee to move a visit the server would have moved for free,
   * and got a new reference number for it.
   *
   * The recreate also needed `serviceId`/`addressId`/`paymentMode` off the
   * cached row, and went to some trouble to fetch them when the list came
   * back thin. None of that is needed now: the server already knows what the
   * booking is, and moving it changes only its slot.
   *
   * The row comes back moved, so this replaces it in place exactly as
   * `cancel` does - same id, so the list and any open detail screen are both
   * still pointing at something that exists.
   */
  const reschedule = useCallback<UseBookings['reschedule']>(
    async (id, slotStartAt) => {
      const moved = toAppBooking(
        await rescheduleBooking(id, slotStartAt),
        catalogue.current.services,
        catalogue.current.categoryLabel,
      );

      if (alive.current) {
        setBookings(current =>
          current.map(booking => (booking.id === id ? moved : booking)),
        );
      }

      return moved;
    },
    [],
  );

  /* Asked at the moment the sheet opens, never cached - see the type. */
  const rescheduleAllowance = useCallback<UseBookings['rescheduleAllowance']>(
    (id, signal) => fetchRescheduleAllowance(id, signal),
    [],
  );

  /**
   * Rate the professional, then re-read the booking.
   *
   * -------------------------------------------------------------------------
   * WHY THE RESPONSE IS NOT MAPPED
   * -------------------------------------------------------------------------
   * It used to be: the result went straight into `toAppBooking` and replaced
   * the row in the list. But `POST /bookings/:id/review` answers with the
   * REVIEW it just wrote, not with the booking - no service, no status, no
   * slot, no professional. So a review that succeeded left a husk on screen
   * where the booking card had been, and nothing said why.
   *
   * `refreshOne` instead. The booking's own read is the only thing that knows
   * how to describe a booking, and it comes back carrying the review as the
   * server now holds it - which is also how the stars redraw without this
   * function having to guess at the arithmetic.
   */
  const rate = useCallback<UseBookings['rate']>(
    async (id, input) => {
      const { photos, ...review } = input;

      /*
       * Photographs first, and their failures do not stop the review.
       *
       * `uploadReviewPhotos` keeps only what landed. A customer who wrote five
       * stars and a sentence must not lose both because one PUT timed out on a
       * lift - so the review goes with whatever keys came back, and the rest
       * are simply not attached.
       */
      const photoKeys = photos?.length
        ? await uploadReviewPhotos(id, photos)
        : undefined;

      await reviewBooking(id, {
        ...review,
        ...(photoKeys?.length ? { photoKeys } : {}),
      });
      /* Awaited rather than fired off: the caller closes its sheet on resolve,
         and a list that repaints a moment later reads as a second event. */
      await refreshOne(id);
    },
    [refreshOne],
  );

  return {
    bookings,
    loading,
    error,
    refresh: load,
    refreshOne,
    book,
    placeCheckoutOrder,
    loadBookings,
    cancel,
    reschedule,
    rescheduleAllowance,
    rate,
  };
}
